import axios from 'axios';
import { getHatateToken } from '../helpers/utils';

// Configuración base para el servicio SellerBot
const BASE_URL = 'https://backend.anubisai.net/api';
const SELLERBOT_BASE = `${BASE_URL}/sellerbot`;

// Crear instancia de axios con configuración por defecto
const sellerbotApi = axios.create({
  baseURL: SELLERBOT_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar tokens automáticamente
sellerbotApi.interceptors.request.use((config) => {
  const token = getHatateToken();
  const apiKey = 'anubis-sales-bot-api-key';
  
  if (token) {
    config.headers.Authorization = `${token}`;
  }
  
  if (apiKey) {
    config.headers['x-api-key'] = apiKey;
  }
  
  return config;
});

// Interceptor para manejar errores
sellerbotApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('SellerBot API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export interface Agent {
    id: string;
    name: string;
    age: number;
    gender: 'male' | 'female' | 'neutral';
    personality_type: 'aggressive' | 'consultive' | 'educational' | 'friendly';
    bio: string;
    specialties: string[];
    prompt_template: string;
    avatar_url?: string;
    emoji: string;
    is_active: boolean;
    max_concurrent_contacts: number;
    performance_metrics: Record<string, any>;
    current_metrics?: {
        current_contacts: number;
        active_conversations: number;
        monthly_sales: number;
        monthly_revenue: number;
        capacity_usage: number;
    };
    detailed_metrics?: {
        contacts_by_status: Record<string, number>;
        conversations_by_stage: Record<string, number>;
        monthly_performance: {
            total_conversations: number;
            closed_sales: number;
            revenue: number;
            avg_deal_size: number;
            conversion_rate: number;
        };
    };
    created_at: string;
    updated_at: string;
}

export interface AgentCreate {
    name: string;
    age: number;
    gender: 'male' | 'female' | 'neutral';
    personality_type: 'aggressive' | 'consultive' | 'educational' | 'friendly';
    bio: string;
    specialties: string[];
    prompt_template: string;
    avatar_url?: string;
    emoji: string;
    max_concurrent_contacts: number;
}

export interface AgentUpdate {
    name?: string;
    age?: number;
    bio?: string;
    specialties?: string[];
    prompt_template?: string;
    avatar_url?: string;
    emoji?: string;
    is_active?: boolean;
    max_concurrent_contacts?: number;
}

export interface ContactAssignment {
    contact_ids: string[];
    agent_id: string;
    assignment_type: 'manual' | 'automatic';
    priority: number;
}

export interface CampaignAssignment {
    campaign_id: string;
    agent_id: string;
    assignment_type: 'manual' | 'automatic';
}

export interface AgentMetric {
    agent_id: string;
    name: string;
    emoji: string;
    total_conversations: number;
    closed_sales: number;
    revenue: number;
    assigned_contacts: number;
    conversion_rate: number;
}

export interface AgentResponse<T = any> {
    success: boolean;
    data: T;
    message: string;
}

export interface AgentsListResponse {
    agents: Agent[];
    total: number;
}

export interface AssignmentResponse {
    agent_id: string;
    agent_name: string;
    assigned_count: number;
    total_requested: number;
    errors: string[];
}

export interface DashboardWithAgentsResponse {
    summary: {
        totalConversations: number;
        closedSales: number;
        totalRevenue: number;
        conversionRate: number;
    };
    agentMetrics: AgentMetric[];
    topPerformer: AgentMetric | null;
    timeFilter: string;
    agentFilter?: string;
    lastUpdated: string;
}

export interface ContactValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  phoneIssues: Array<{
    row: number;
    original: string;
    corrected: string;
    issue: string;
  }>;
  duplicates: Array<{
    row: number;
    phone: string;
    name: string;
    reason: string;
  }>;
  totalContacts: number;
  validContacts: number;
}

export interface ProcessedContact {
  id?: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  product_service_interest?: string;
  source: string;
  campaign_id?: string;
  agent_id?: string;
  status: 'new' | 'contacted' | 'interested' | 'closed' | 'rejected';
  created_at?: string;
  row_number?: number;
  has_error?: boolean;
  error_message?: string;
}

export interface ContactUploadResponse {
  success: boolean;
  message: string;
  data: {
    total_processed: number;
    valid_contacts: number;
    saved_contacts: number;
    duplicates_found: number;
    duplicates_updated: number;
    errors: number;
    warnings: string[];
    contact_ids: string[];
    contacts_count?: number;
    total_contacts?: number;
  };
}

export interface SellerbotStatus {
  success: boolean;
  data: {
    enabled: boolean;
    activeProfiles: number;
    runningCampaigns: number;
    version: string;
    lastSync: string;
    uptime: number;
    port: number;
    baseDirectory?: string;
    databasePath?: string;
  };
  message: string;
}

export interface DashboardData {
  success: boolean;
  data: {
    kpis: KPIMetric[];
    timeSeries: TimeSeriesData[];
    funnel: FunnelData[];
    topPerformers: PerformerData[];
  };
  message: string;
}

export interface KPIMetric {
  id: string;
  title: string;
  value: number;
  previousValue?: number;
  change?: number;
  changeType: 'positive' | 'negative' | 'neutral';
  format: 'currency' | 'percentage' | 'number' | 'text';
}

export interface TimeSeriesData {
  date: string;
  revenue: number;
  leads: number;
  conversions: number;
  calls: number;
  emails: number;
}

export interface FunnelData {
  stage: string;
  count: number;
  value: number;
  conversionRate: number;
}

export interface PerformerData {
  id: string;
  name: string;
  type: string;
  revenue: number;
  conversions: number;
  score: number;
}

export interface SalesProfile {
  id: string;
  name: string;
  personality: 'aggressive' | 'consultive' | 'educational' | 'friendly';
  gender: 'male' | 'female' | 'neutral';
  ageRange: string;
  specialty: string;
  language: string;
  description: string;
  isActive: boolean;
  conversionsToday: number;
  leadsContacted: number;
  successRate: number;
  avgResponseTime: string;
  createdAt: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: 'service' | 'product' | 'platform';
  closingMethod: 'qr' | 'link' | 'email' | 'phone';
  targetAudience: string;
  features: string[];
  isActive: boolean;
  salesCount: number;
  conversionRate: number;
  createdAt: string;
}

export interface SalesConversation {
  whatsappChatId: any;
  isActive: unknown;
  id: string;
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  profileUsed: string;
  productOffered: string;
  stage: 'prospecting' | 'interest' | 'negotiation' | 'closing' | 'closed' | 'lost';
  lastMessage: string;
  lastMessageTime: string;
  value: number;
  source: string;
  messages: ConversationMessage[];
  notes: string;
  probability: number;
}

export interface ConversationMessage {
  id: string;
  sender: 'bot' | 'client';
  message: string;
  timestamp: string;
  type: 'text' | 'image' | 'document';
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  profileId: string;
  productId: string;
  agentId?: string;           // ✅ AGREGAR
  targetList: string;
  status: 'running' | 'paused' | 'completed' | 'scheduled' | 'draft';  // ✅ Agregar 'draft'
  startDate: string;
  endDate?: string;
  totalContacts: number;
  contactsReached: number;
  leadsGenerated: number;
  salesClosed: number;
  revenue: number;
  schedule: ScheduleConfig;
  profileName?: string;
  productName?: string;
  agentName?: string;         // ✅ AGREGAR
  
  // Nuevas propiedades para configuración avanzada
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  maxContactsPerHour?: number;
  workingHours?: {
    start: string;
    end: string;
  };
  workingDays?: string[];
  autoAssignContacts?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ScheduleConfig {
  enabled: boolean;
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
  maxContactsPerHour: number;
  delayBetweenMessages: number;
}

export interface RoutineTask {
  id: string;
  name: string;
  type: 'follow_up' | 'lead_generation' | 'data_sync' | 'report_generation' | 'auto_detect' | 'campaign_trigger';
  description: string;
  schedule: string;
  isActive: boolean;
  lastRun?: string;
  nextRun?: string;
  status: 'running' | 'idle' | 'error';
  config: Record<string, any>;
  createdAt: string;
}

const mapCampaignFromBackend = (backendCampaign: any): Campaign => {
  return {
    id: backendCampaign.id,
    name: backendCampaign.name,
    description: backendCampaign.description,
    profileId: backendCampaign.profile_id || backendCampaign.profileId,
    productId: backendCampaign.product_id || backendCampaign.productId,
    agentId: backendCampaign.agent_id || backendCampaign.agentId,
    targetList: backendCampaign.target_list || backendCampaign.targetList || '',
    status: backendCampaign.status,
    startDate: backendCampaign.start_date || backendCampaign.startDate,
    endDate: backendCampaign.end_date || backendCampaign.endDate,
    totalContacts: backendCampaign.total_contacts || backendCampaign.totalContacts || 0,
    contactsReached: backendCampaign.contacts_reached || backendCampaign.contactsReached || 0,
    leadsGenerated: backendCampaign.leads_generated || backendCampaign.leadsGenerated || 0,
    salesClosed: backendCampaign.sales_closed || backendCampaign.salesClosed || 0,
    revenue: backendCampaign.revenue || 0,
    priority: backendCampaign.priority,
    maxContactsPerHour: backendCampaign.max_contacts_per_hour || backendCampaign.maxContactsPerHour,
    workingHours: backendCampaign.working_hours || backendCampaign.workingHours,
    workingDays: backendCampaign.working_days || backendCampaign.workingDays,
    autoAssignContacts: backendCampaign.auto_assign_contacts || backendCampaign.autoAssignContacts,
    schedule: backendCampaign.schedule || {
      enabled: false,
      startTime: '09:00',
      endTime: '18:00',
      daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      maxContactsPerHour: 10,
      delayBetweenMessages: 30
    },
    profileName: backendCampaign.profile_name || backendCampaign.profileName,
    productName: backendCampaign.product_name || backendCampaign.productName,
    agentName: backendCampaign.agent_name || backendCampaign.agentName,
    createdAt: backendCampaign.created_at || backendCampaign.createdAt,
    updatedAt: backendCampaign.updated_at || backendCampaign.updatedAt
  };
};

// ==================== FUNCIÓN DE MAPEO FRONTEND → BACKEND ====================

const mapCampaignToBackend = (frontendCampaign: any) => {
  return {
    name: frontendCampaign.name,
    description: frontendCampaign.description,
    agent_id: frontendCampaign.agentId || null,
    profile_id: frontendCampaign.profileId || null,
    product_id: frontendCampaign.productId || null,
    priority: frontendCampaign.priority || 'medium',
    max_contacts_per_hour: frontendCampaign.maxContactsPerHour || 10,
    working_hours: frontendCampaign.workingHours || { start: '09:00', end: '18:00' },
    working_days: frontendCampaign.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    auto_assign_contacts: frontendCampaign.autoAssignContacts || false,
    status: frontendCampaign.status || 'draft'
  };
};

// Servicio principal de SellerBot
export class SellerbotService {
  
  // ==================== MÉTODOS PRINCIPALES ====================

  /**
   * Alternar estado de rutina (activar/desactivar)
   */
  static async toggleRoutine(routineId: string): Promise<any> {
    const response = await sellerbotApi.patch(`/routines/${routineId}/toggle`);
    return response.data;
  }

  /**
   * Ejecutar rutina manualmente
   */
  static async executeRoutine(routineId: string): Promise<any> {
    const response = await sellerbotApi.post(`/routines/${routineId}/execute`);
    return response.data;
  }

  /**
   * Eliminar rutina
   */
  static async deleteRoutine(routineId: string): Promise<any> {
    const response = await sellerbotApi.delete(`/routines/${routineId}`);
    return response.data;
  }

  /**
   * Actualizar rutina existente
   */
  static async updateRoutine(routineId: string, routine: Partial<RoutineTask>): Promise<any> {
    const response = await sellerbotApi.put(`/routines/${routineId}`, routine);
    return response.data;
  }

  /**
   * Obtener rutina específica
   */
  static async getRoutine(routineId: string): Promise<{ success: boolean; data: { routine: RoutineTask } }> {
    const response = await sellerbotApi.get(`/routines/${routineId}`);
    return response.data;
  }
  
  /**
   * Verificar el estado de salud del SellerBot
   */
  static async checkHealth(): Promise<any> {
    const response = await sellerbotApi.get('/health');
    return response.data;
  }

  /**
   * Obtener el estado actual del SellerBot
   */
  static async getStatus(): Promise<SellerbotStatus> {
    const response = await sellerbotApi.get('/status');
    return response.data;
  }

  /**
   * Activar o desactivar el SellerBot
   */
  static async toggleBot(enabled: boolean, p0: string): Promise<any> {
    const response = await sellerbotApi.post('/toggle', { enabled });
    return response.data;
  }

  /**
   * Obtener datos del dashboard CRM
   */
  static async getDashboardData(timeFilter: string = '7d'): Promise<DashboardData> {
    const response = await sellerbotApi.get('/dashboard', { 
      params: { timeFilter } 
    });
    return response.data;
  }

  // ==================== GESTIÓN DE CONVERSACIONES ====================

  /**
   * Obtener conversaciones de ventas
   */
  static async getConversations(params: {
    status?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ success: boolean; data: { conversations: SalesConversation[]; total: number } }> {
    const response = await sellerbotApi.get('/conversations', { params });
    return response.data;
  }

  /**
   * Enviar mensaje a una conversación específica
   */
  static async sendMessageToConversation(
    conversationId: string, 
    message: string, 
    sender: string = 'bot'
  ): Promise<any> {
    const response = await sellerbotApi.post(`/conversations/${conversationId}/message`, {
      message,
      sender,
      message_type: 'text'
    });
    return response.data;
  }

  // ==================== GESTIÓN DE PERFILES IA ====================

  /**
   * Obtener perfiles de vendedores IA
   */
  static async getProfiles(): Promise<{ success: boolean; data: { profiles: SalesProfile[] } }> {
    const response = await sellerbotApi.get('/profiles');
    return response.data;
  }

  /**
   * Crear nuevo perfil de vendedor IA
   */
  static async createProfile(profile: Omit<SalesProfile, 'id' | 'conversionsToday' | 'leadsContacted' | 'successRate' | 'avgResponseTime' | 'createdAt'>): Promise<any> {
    const response = await sellerbotApi.post('/profiles', profile);
    return response.data;
  }

  // ==================== GESTIÓN DE PRODUCTOS ====================

  /**
   * Obtener productos y servicios
   */
  static async getProducts(): Promise<{ success: boolean; data: { products: Product[] } }> {
    const response = await sellerbotApi.get('/products');
    return response.data;
  }

  /**
   * Crear nuevo producto
   */
  static async createProduct(product: Omit<Product, 'id' | 'salesCount' | 'conversionRate' | 'createdAt'>): Promise<any> {
    const response = await sellerbotApi.post('/products', product);
    return response.data;
  }

  // ==================== GESTIÓN DE CAMPAÑAS ====================

/**
 * Actualizar campaña existente
 */
static async updateCampaign(campaignId: string, campaign: any): Promise<any> {
  try {
    const response = await sellerbotApi.put(`/campaigns/${campaignId}`, campaign);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error updating campaign:', error);
    throw error;
  }
}

  /**
   * Obtener campañas de marketing
   */
static async getCampaigns(): Promise<{ success: boolean; data: { campaigns: Campaign[] } }> {
  try {
    const response = await sellerbotApi.get('/campaigns');
    
    // Mapear cada campaña del backend al formato frontend
    const campaigns = response.data.data.campaigns.map(mapCampaignFromBackend);
    
    return {
      success: response.data.success,
      data: { campaigns }
    };
  } catch (error: any) {
    console.error('❌ Error fetching campaigns:', error);
    throw error;
  }
}

  /**
   * Crear nueva campaña
   */
static async createCampaign(campaignData: any): Promise<any> {
  try {
    // ✅ Validar que end_date sea una fecha válida o null
    let endDate: string | null = null;
    if (campaignData.end_date) {
      const parsedEndDate = new Date(campaignData.end_date);
      if (!isNaN(parsedEndDate.getTime())) {
        endDate = parsedEndDate.toISOString();
      }
    }

    const backendData = {
      name: campaignData.name,
      description: campaignData.description,
      profile_id: campaignData.profile_id,
      product_id: campaignData.product_id,
      target_list: campaignData.target_list || 'general',
      status: campaignData.status || 'scheduled',
      start_date: campaignData.start_date,
      end_date: endDate, // ✅ Siempre será null o una fecha ISO válida
      schedule_config: campaignData.schedule_config || '{}'
    };

    console.log('📤 Enviando al backend (datos validados):', backendData);

    const response = await sellerbotApi.post('/campaigns', backendData);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating campaign:', error);
    // ✅ Mejorar el manejo de errores
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw error;
    }
  }
}

/**
 * Eliminar campaña
 */
static async deleteCampaign(campaignId: string): Promise<any> {
  try {
    const response = await sellerbotApi.delete(`/campaigns/${campaignId}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error deleting campaign:', error);
    throw error;
  }
}
  /**
   * Iniciar o pausar campaña
   */
  static async toggleCampaign(campaignId: string): Promise<any> {
    try {
        const response = await sellerbotApi.post(`/campaigns/${campaignId}/toggle`, {}, {
      timeout: 45000 // ✅ 45 segundos para toggle
    });
        return response.data;
    } catch (error: any) {
        console.error('❌ Error toggling campaign:', error);
        throw error;
    }
}

/**
 * Asignar contactos a campaña
 */
static async assignContactsToCampaign(campaignId: string, assignmentData: any): Promise<any> {
  try {
    const response = await sellerbotApi.post(`/campaigns/${campaignId}/assign-contacts`, assignmentData);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error assigning contacts to campaign:', error);
    throw error;
  }
}

  // ==================== GESTIÓN DE RUTINAS ====================

  /**
   * Obtener estadísticas de rutinas
   */
  static async getRoutineStats(): Promise<{
    total: number;
    active: number;
    running: number;
    failed: number;
    executions_today: number;
  }> {
    const response = await sellerbotApi.get('/routines/stats');
    return response.data;
  }

  /**
   * Obtener logs de una rutina específica
   */
  static async getRoutineLogs(routineId: string, limit: number = 50): Promise<{
    logs: Array<{
      timestamp: string;
      level: 'info' | 'warning' | 'error';
      message: string;
      execution_id?: string;
    }>;
  }> {
    const response = await sellerbotApi.get(`/routines/${routineId}/logs`, {
      params: { limit }
    });
    return response.data;
  }

  /**
   * Obtener rutinas automatizadas
   */
  static async getRoutines(): Promise<{ success: boolean; data: { routines: RoutineTask[] } }> {
    const response = await sellerbotApi.get('/routines');
    return response.data;
  }

  /**
   * Crear nueva rutina
   */
  static async createRoutine(routine: Omit<RoutineTask, 'id' | 'lastRun' | 'nextRun' | 'status' | 'createdAt'>): Promise<any> {
    const response = await sellerbotApi.post('/routines', routine);
    return response.data;
  }

  // ==================== GESTIÓN DE CONTACTOS ====================

static async uploadContactsData(
    contacts: any[],
    options?: {
      campaign_id?: string;
      source?: string;
    }
  ): Promise<ContactUploadResponse> {
    
    console.log('=== UPLOAD CONTACTS DATA ===');
    console.log('Input contacts:', contacts.length);
    
    // Validar entrada
    if (!Array.isArray(contacts)) {
      throw new Error('Contacts must be an array');
    }
    
    if (contacts.length === 0) {
      throw new Error('No contacts provided');
    }
    
    // Validar y limpiar contactos
    const validatedContacts = contacts
      .slice(0, 50)
      .map((contact) => {
        const cleaned = {
          name: String(contact.name || contact.nombre || '').trim(),
          phone: String(contact.phone || contact.telefono || contact.teléfono || '').trim(),
          email: String(contact.email || contact.correo || '').trim(),
          company: String(contact.company || contact.empresa || '').trim(),
          product_service_interest: String(
            contact.product_service_interest || 
            contact.product || 
            contact.producto || 
            contact.course || 
            contact.curso || 
            ''
          ).trim(),
          source: String(contact.source || 'csv_upload')
        };
        
        // Solo retornar contactos válidos
        if (cleaned.name && cleaned.phone) {
          return cleaned;
        }
        return null;
      })
      .filter(contact => contact !== null);
    
    if (validatedContacts.length === 0) {
      throw new Error('No valid contacts after validation');
    }

    // Crear payload para el SellerBot
    const payload: any = {
      contacts: validatedContacts,
      source: options?.source || 'csv_upload'
    };
    
    // Solo agregar campaign_id si es válido
    if (options?.campaign_id && typeof options.campaign_id === 'string' && options.campaign_id.trim()) {
      payload.campaign_id = options.campaign_id.trim();
    }

    console.log('Sending to backend:', {
      contacts_count: payload.contacts.length,
      has_campaign_id: !!payload.campaign_id,
      source: payload.source
    });
    
    try {
      // ✅ USAR EL NUEVO ENDPOINT JSON
      const response = await sellerbotApi.post('/upload-contacts-json', payload, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 120000
      });
      
      console.log('✅ Upload successful:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Upload failed:', error.response?.data);
      
      if (error.response?.status === 422) {
        const details = error.response.data?.detail || [];
        const errorMsg = Array.isArray(details) 
          ? details.map((d: any) => `${d.loc?.join('.')} - ${d.msg}`).join(', ')
          : JSON.stringify(error.response.data);
        throw new Error(`Validation Error: ${errorMsg}`);
      }
      
      if (error.response?.status === 404) {
        throw new Error('Endpoint not found. Please update SellerBot to latest version.');
      }
      
      throw error;
    }
  }

  /**
   * Validar archivo de contactos antes de subir
   */
  static async validateContactFile(file: File): Promise<ContactValidationResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_only', 'true');
    
    const response = await sellerbotApi.post('/validate-contacts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000
    });
    
    return response.data;
  }

  /**
   * Verificar duplicados de contactos
   */
  static async checkDuplicateContacts(
    contacts: Array<{ phone: string; name: string }>
  ): Promise<{
    duplicates: Array<{
      phone: string;
      name: string;
      existing_contact: ProcessedContact;
      action_suggested: 'update' | 'skip';
    }>;
    new_contacts: number;
  }> {
    const response = await sellerbotApi.post('/check-duplicates', { contacts });
    return response.data;
  }

  /**
 * Eliminar contacto
 */
static async deleteContact(contactId: string): Promise<any> {
  try {
    const response = await sellerbotApi.delete(`/contacts/${contactId}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error deleting contact:', error);
    throw error;
  }
}

static async getContacts(params: {
    agent_id?: string;
    campaign_id?: string;
    status?: string;
    source?: string;
    limit?: number;
    offset?: number;
    search?: string;
  } = {}): Promise<{
    success: boolean;
    data: {
      contacts: ProcessedContact[];
      total: number;
      filters_applied: string[];
    };
  }> {
    const response = await sellerbotApi.get('/contacts', { params });
    return response.data;
  }

  /**
   * Asignar contactos a un agente específico
   */
  static async assignContactsToAgent(
    contact_ids: string[],
    agent_id: string,
    campaign_id?: string
  ): Promise<{
    success: boolean;
    assigned_count: number;
    failed_assignments: string[];
  }> {
    const response = await sellerbotApi.post('/assign-contacts', {
      contact_ids,
      agent_id,
      campaign_id
    });
    return response.data;
  }

  /**
   * Reasignar contactos entre agentes
   */
  static async reassignContacts(
    contact_ids: string[],
    from_agent_id: string,
    to_agent_id: string
  ): Promise<{
    success: boolean;
    reassigned_count: number;
    message: string;
  }> {
    const response = await sellerbotApi.post('/reassign-contacts', {
      contact_ids,
      from_agent_id,
      to_agent_id
    });
    return response.data;
  }

   /**
   * Obtener estadísticas de contactos por agente
   */
  static async getContactStats(agent_id?: string): Promise<{
    total_contacts: number;
    by_status: Record<string, number>;
    by_source: Record<string, number>;
    by_campaign: Record<string, number>;
    recent_activity: Array<{
      date: string;
      new_contacts: number;
      contacted: number;
      conversions: number;
    }>;
  }> {
    const params = agent_id ? { agent_id } : {};
    const response = await sellerbotApi.get('/contact-stats', { params });
    return response.data;
  }

  /**
 * Exportar contactos
 */
static async exportContacts(params: {
    agent_id?: string;
    campaign_id?: string;
    status?: string;
    format?: 'csv' | 'xlsx' | 'json';
    include_stats?: boolean;
  } = {}): Promise<{
    success: boolean;
    download_url: string;
    filename: string;
    total_exported: number;
  }> {
    const response = await sellerbotApi.post('/export-contacts', params);
    return response.data;
  }

  // ==================== GESTIÓN DE ARCHIVOS DE CONTACTOS ====================
  
  /**
   * Obtener historial de uploads de contactos
   */
  static async getUploadHistory(params: {
  agent_id?: string;
  campaign_id?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{
  success: boolean;
  data: {
    uploads: Array<{
      id: string;
      filename: string;
      upload_date: string;
      agent_id?: string;
      campaign_id?: string;
      total_contacts: number;
      valid_contacts: number;
      duplicates_found: number;
      status: 'success' | 'error' | 'partial';
      error_message?: string;
      file_size?: number;
      processing_time_ms?: number;
      source: string;
    }>;
    total: number;
    limit: number;
    offset: number;
  };
  message: string;
}> {
  try {
    const response = await sellerbotApi.get('/upload-history', { 
      params: {
        agent_id: params.agent_id,
        campaign_id: params.campaign_id,
        limit: params.limit || 10,
        offset: params.offset || 0
      }
    });
    
    console.log('✅ Upload history retrieved:', response.data);
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error getting upload history:', error);
    
    // Fallback a datos simulados si el endpoint no está disponible
    if (error.response?.status === 404 || error.response?.status === 500) {
      console.log('📝 Using fallback mock data for upload history');
      
      return {
        success: true,
        data: {
          uploads: [
            {
              id: `mock_${Date.now()}`,
              filename: 'recent_upload.csv',
              upload_date: new Date().toISOString(),
              total_contacts: 0,
              valid_contacts: 0,
              duplicates_found: 0,
              status: 'success' as const,
              source: 'csv_upload'
            }
          ],
          total: 1,
          limit: params.limit || 10,
          offset: params.offset || 0
        },
        message: 'Historial simulado - endpoint no disponible'
      };
    }
    
    throw error;
  }
}

  /**
   * Eliminar upload y todos sus contactos
   */
  static async deleteUploadHistory(
  upload_id: string, 
  delete_contacts: boolean = false
): Promise<{
  success: boolean;
  data: {
    deleted_upload: string;
    deleted_contacts: number;
  };
  message: string;
}> {
  try {
    const response = await sellerbotApi.delete(`/upload-history/${upload_id}`, {
      params: { delete_contacts }
    });
    
    console.log('✅ Upload history deleted:', upload_id);
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error deleting upload history:', error);
    throw error;
  }
}

  
  // ==================== CONFIGURACIÓN Y MONITOREO ====================

  /**
   * Obtener configuración actual
   */
  static async getConfiguration(): Promise<any> {
    const response = await sellerbotApi.get('/configuration');
    return response.data;
  }

  /**
   * Actualizar configuración
   */
  static async updateConfiguration(config: any): Promise<any> {
    const response = await sellerbotApi.post('/configure', config);
    return response.data;
  }

  /**
   * Ping rápido al SellerBot
   */
  static async ping(): Promise<any> {
    const response = await sellerbotApi.get('/ping');
    return response.data;
  }

  /**
   * Obtener estadísticas rápidas
   */
  static async getStats(): Promise<any> {
    const response = await sellerbotApi.get('/stats');
    return response.data;
  }

  /**
   * Obtener logs recientes
   */
  static async getLogs(lines: number = 50): Promise<any> {
    const response = await sellerbotApi.get('/logs', { 
      params: { lines } 
    });
    return response.data;
  }

  /**
   * Reiniciar SellerBot
   */
  static async restart(): Promise<any> {
    const response = await sellerbotApi.post('/restart');
    return response.data;
  }

  /**
   * Parar SellerBot
   */
  static async stop(): Promise<any> {
    const response = await sellerbotApi.post('/stop');
    return response.data;
  }

  /**
   * Obtener documentación de endpoints
   */
  static async getEndpoints(): Promise<any> {
    const response = await sellerbotApi.get('/endpoints');
    return response.data;
  }
}

// ==================== HOOKS DE REACT ====================

import { useState, useEffect } from 'react';

/**
 * Hook para obtener el estado del SellerBot
 */
export const useSellerbotStatus = (autoRefresh: boolean = true, interval: number = 30000) => {
  const [status, setStatus] = useState<SellerbotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      setError(null);
      const response = await SellerbotService.getStatus();
      setStatus(response);
    } catch (err: any) {
      setError(err.message || 'Error fetching SellerBot status');
      console.error('Error fetching SellerBot status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();

    if (autoRefresh) {
      const intervalId = setInterval(fetchStatus, interval);
      return () => clearInterval(intervalId);
    }
  }, [autoRefresh, interval]);

  return { status, loading, error, refetch: fetchStatus };
};

/**
 * Hook para obtener datos del dashboard
 */
export const useSellerbotDashboard = (timeFilter: string = '7d', autoRefresh: boolean = true) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    try {
      setError(null);
      const response = await SellerbotService.getDashboardData(timeFilter);
      setDashboardData(response);
    } catch (err: any) {
      setError(err.message || 'Error fetching dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();

    if (autoRefresh) {
      const intervalId = setInterval(fetchDashboard, 60000); // Cada minuto
      return () => clearInterval(intervalId);
    }
  }, [timeFilter, autoRefresh]);

  return { dashboardData, loading, error, refetch: fetchDashboard };
};

/**
 * Hook para gestionar conversaciones
 */
export const useSellerbotConversations = (params: { status?: string; limit?: number; } = {}, p0: boolean) => {
  const [conversations, setConversations] = useState<SalesConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchConversations = async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await SellerbotService.getConversations(params);
      setConversations(response.data.conversations);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.message || 'Error fetching conversations');
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [params.status, params.limit]);

  return { conversations, total, loading, error, refetch: fetchConversations };
};

/**
 * Hook para gestión completa de contactos con validación
 */
export const useContactManagement = (agent_id?: string, campaign_id?: string) => {
  const [contacts, setContacts] = useState<ProcessedContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ContactValidationResult | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Cargar contactos con filtros
  const loadContacts = async (filters?: any) => {
    setLoading(true);
    try {
      const params = { agent_id, campaign_id, ...filters };
      const response = await SellerbotService.getContacts(params);
      setContacts(response.data.contacts);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error loading contacts');
    } finally {
      setLoading(false);
    }
  };

  // Validar archivo antes de subir
  const validateFile = async (file: File) => {
    setLoading(true);
    try {
      const result = await SellerbotService.validateContactFile(file);
      setValidationResult(result);
      setError(null);
      return result;
    } catch (err: any) {
      setError(err.message || 'Error validating file');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Subir archivo con progreso
const uploadFile = async (
  file: File, 
  options?: { 
    agent_id?: string; 
    campaign_id?: string; 
    update_duplicates?: boolean;
  }
) => {
  setLoading(true);
  setUploadProgress(0);
  
  try {
    // ❌ NO HACER ESTO (era el error):
    // const formData = new FormData();
    // formData.append('file', file);
    // const response = await SellerbotService.uploadContactsData(formData, {...});
    
    // ✅ HACER ESTO (método correcto):
    // 1. Procesar archivo localmente primero
    const processedContacts = await processFileLocally(file);
    
    // 2. Enviar contactos como JSON
    const campaignIdToUse = campaign_id || options?.campaign_id;
const uploadOptions: any = {
  source: 'csv_upload'
};

if (campaignIdToUse && typeof campaignIdToUse === 'string' && campaignIdToUse.trim()) {
  uploadOptions.campaign_id = campaignIdToUse.trim();
}

const response = await SellerbotService.uploadContactsData(processedContacts, uploadOptions);
    
    setUploadProgress(100);
    await loadContacts(); // Recargar contactos
    setError(null);
    return response;
    
  } catch (err: any) {
    setError(err.message || 'Error uploading file');
    setUploadProgress(0);
    throw err;
  } finally {
    setLoading(false);
  }
};

const processFileLocally = async (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const lines = csv.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          reject(new Error('Empty CSV file'));
          return;
        }
        
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        const contacts = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const contact: any = {};
          
          headers.forEach((header, i) => {
            contact[header.toLowerCase()] = values[i] || '';
          });
          
          // Mapear campos comunes
          return {
            name: contact.name || contact.nombre || '',
            phone: contact.phone || contact.telefono || contact.teléfono || '',
            email: contact.email || contact.correo || '',
            company: contact.company || contact.empresa || '',
            product_service_interest: contact.product || contact.producto || contact.course || contact.curso || '',
            source: 'csv_upload'
          };
        }).filter(c => c.name && c.phone);
        
        resolve(contacts);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};

  return {
    contacts,
    loading,
    error,
    validationResult,
    uploadProgress,
    loadContacts,
    validateFile,
    uploadFile,
    setError
  };
};

/**
 * Hook para estadísticas de contactos
 */
export const useContactStats = (agent_id?: string, autoRefresh: boolean = true) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      const response = await SellerbotService.getContactStats(agent_id);
      setStats(response);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error fetching contact stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    
    if (autoRefresh) {
      const interval = setInterval(fetchStats, 60000); // Cada minuto
      return () => clearInterval(interval);
    }
  }, [agent_id, autoRefresh]);

  return { stats, loading, error, refetch: fetchStats };
};

/**
 * Hook para gestionar perfiles IA
 */
export const useSellerbotProfiles = () => {
  const [profiles, setProfiles] = useState<SalesProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = async () => {
    try {
      setError(null);
      const response = await SellerbotService.getProfiles();
      setProfiles(response.data.profiles);
    } catch (err: any) {
      setError(err.message || 'Error fetching profiles');
      console.error('Error fetching profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async (profile: Omit<SalesProfile, 'id' | 'conversionsToday' | 'leadsContacted' | 'successRate' | 'avgResponseTime' | 'createdAt'>) => {
    try {
      await SellerbotService.createProfile(profile);
      await fetchProfiles(); // Refrescar lista
    } catch (err: any) {
      setError(err.message || 'Error creating profile');
      throw err;
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  return { profiles, loading, error, refetch: fetchProfiles, createProfile };
};

/**
 * Hook para gestionar productos
 */
export const useSellerbotProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setError(null);
      const response = await SellerbotService.getProducts();
      setProducts(response.data.products);
    } catch (err: any) {
      setError(err.message || 'Error fetching products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const createProduct = async (product: Omit<Product, 'id' | 'salesCount' | 'conversionRate' | 'createdAt'>) => {
    try {
      await SellerbotService.createProduct(product);
      await fetchProducts(); // Refrescar lista
    } catch (err: any) {
      setError(err.message || 'Error creating product');
      throw err;
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return { products, loading, error, refetch: fetchProducts, createProduct };
};

/**
 * Hook para gestionar campañas
 */
export const useSellerbotCampaigns = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    try {
      setError(null);
      const response = await SellerbotService.getCampaigns();
      setCampaigns(response.data.campaigns);
    } catch (err: any) {
      setError(err.message || 'Error fetching campaigns');
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (campaign: Omit<Campaign, 'id' | 'totalContacts' | 'contactsReached' | 'leadsGenerated' | 'salesClosed' | 'revenue' | 'profileName' | 'productName' | 'agentName'>) => {
  try {
    await SellerbotService.createCampaign(campaign);
    await fetchCampaigns(); // Refrescar lista
  } catch (err: any) {
    setError(err.message || 'Error creating campaign');
    throw err;
  }
};

  const toggleCampaign = async (campaignId: string) => {
    try {
      await SellerbotService.toggleCampaign(campaignId);
      await fetchCampaigns(); // Refrescar lista
    } catch (err: any) {
      setError(err.message || 'Error toggling campaign');
      throw err;
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  return { campaigns, loading, error, refetch: fetchCampaigns, createCampaign, toggleCampaign };
};

/**
 * Hook para gestionar rutinas
 */
export const useSellerbotRoutines = () => {
  const [routines, setRoutines] = useState<RoutineTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoutines = async () => {
    try {
      setError(null);
      const response = await SellerbotService.getRoutines();
      setRoutines(response.data.routines || []); // ✅ Fallback a array vacío
    } catch (err: any) {
      setError(err.message || 'Error fetching routines');
      console.error('Error fetching routines:', err);
      setRoutines([]); // ✅ En caso de error, array vacío
    } finally {
      setLoading(false);
    }
  };

  const createRoutine = async (routine: any) => { // ✅ Permitir any para flexibilidad
    try {
      await SellerbotService.createRoutine(routine);
      await fetchRoutines(); // Refrescar lista
    } catch (err: any) {
      setError(err.message || 'Error creating routine');
      throw err;
    }
  };

  const deleteRoutine = async (routineId: string) => { // ✅ NUEVO
    try {
      await SellerbotService.deleteRoutine(routineId);
      await fetchRoutines(); // Refrescar lista
    } catch (err: any) {
      setError(err.message || 'Error deleting routine');
      throw err;
    }
  };

  const toggleRoutine = async (routineId: string) => { // ✅ NUEVO
    try {
      await SellerbotService.toggleRoutine(routineId);
      await fetchRoutines(); // Refrescar lista
    } catch (err: any) {
      setError(err.message || 'Error toggling routine');
      throw err;
    }
  };

  const executeRoutine = async (routineId: string) => { // ✅ NUEVO
    try {
      await SellerbotService.executeRoutine(routineId);
      // No refrescar automáticamente, solo notificar éxito
    } catch (err: any) {
      setError(err.message || 'Error executing routine');
      throw err;
    }
  };

  useEffect(() => {
    fetchRoutines();
  }, []);

  return { 
    routines, 
    loading, 
    error, 
    refetch: fetchRoutines, 
    createRoutine,
    deleteRoutine,
    toggleRoutine, 
    executeRoutine
  };
};

// ==================== UTILIDADES ====================

/**
 * Formatear moneda
 */
export const formatCurrency = (amount: number, currency: string = 'CLP'): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Formatear números
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

/**
 * Formatear fechas
 */
export const formatDate = (dateString: string, language: string = 'es'): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

/**
 * Obtener color para etapa de venta
 */
export const getStageColor = (stage: string): string => {
  switch (stage) {
    case 'prospecting': return 'secondary';
    case 'interest': return 'info';
    case 'negotiation': return 'warning';
    case 'closing': return 'primary';
    case 'closed': return 'success';
    case 'lost': return 'danger';
    default: return 'secondary';
  }
};

/**
 * Traducir etapa de venta
 */
export const translateStage = (stage: string, language: string = 'es'): string => {
  const translations: { [key: string]: { [key: string]: string } } = {
    prospecting: { es: 'Prospectando', en: 'Prospecting' },
    interest: { es: 'Interesado', en: 'Interest' },
    negotiation: { es: 'Negociación', en: 'Negotiation' },
    closing: { es: 'Cerrando', en: 'Closing' },
    closed: { es: 'Cerrado', en: 'Closed' },
    lost: { es: 'Perdido', en: 'Lost' }
  };
  return translations[stage]?.[language] || stage;
};

/**
 * Formatear valor según tipo
 */
export const getFormattedValue = (metric: KPIMetric): string => {
  switch (metric.format) {
    case 'currency':
      return formatCurrency(metric.value as number);
    case 'percentage':
      return `${metric.value}%`;
    case 'number':
      return formatNumber(metric.value as number);
    default:
      return metric.value.toString();
  }
};

export default SellerbotService;
